import { useStore } from '../store/useStore';

interface HeatMapData {
  group: string;
  dimension: string;
  score: number;
}

export function HeatMap() {
  const { studentDataList } = useStore();

  if (studentDataList.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">小组热力图</h3>
        <div className="h-64 flex items-center justify-center text-gray-400">
          暂无数据，请先上传Excel文件
        </div>
      </div>
    );
  }

  const groupMap = new Map<string, { total: number; count: number }>();
  const dimensions = ['运动参与', '技能学习', '健康行为', '体育品德'];

  for (const data of studentDataList) {
    const groupId = data.student.groupId;
    if (!groupMap.has(groupId)) {
      groupMap.set(groupId, { total: 0, count: 0 });
    }
    const group = groupMap.get(groupId)!;
    group.total +=
      data.selfEval.q1Participation +
      data.selfEval.q2Resilience +
      data.selfEval.q3SkillKnow +
      data.selfEval.q4SkillImprove +
      data.selfEval.q5HealthSafe +
      data.selfEval.q6HealthPrep +
      data.selfEval.q7MoralQueue +
      data.selfEval.q8MoralEncourage;
    group.count += 8;
  }

  const groups = Array.from(groupMap.keys()).sort();
  const heatMapData: HeatMapData[] = [];

  for (const group of groups) {
    const groupData = groupMap.get(group)!;
    const avgScore = groupData.count > 0 ? groupData.total / groupData.count : 0;
    for (const dim of dimensions) {
      heatMapData.push({
        group,
        dimension: dim,
        score: avgScore,
      });
    }
  }

  const getColor = (score: number): string => {
    if (score >= 3.5) return 'bg-green-500';
    if (score >= 3.0) return 'bg-green-400';
    if (score >= 2.5) return 'bg-yellow-400';
    if (score >= 2.0) return 'bg-orange-400';
    return 'bg-red-400';
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">小组表现热力图</h3>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="p-2 text-left text-sm font-medium text-gray-600 border-b">组别</th>
              {dimensions.map((dim) => (
                <th key={dim} className="p-2 text-center text-sm font-medium text-gray-600 border-b">
                  {dim}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <tr key={group} className="hover:bg-gray-50">
                <td className="p-2 text-sm font-medium text-gray-800 border-b">{group}</td>
                {dimensions.map((dim) => {
                  const data = heatMapData.find(
                    (d) => d.group === group && d.dimension === dim
                  );
                  const score = data?.score ?? 0;
                  return (
                    <td
                      key={dim}
                      className={`p-2 text-center text-sm font-medium text-white border-b ${getColor(score)}`}
                    >
                      {score.toFixed(2)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex items-center justify-center gap-4 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-red-400 rounded" />
          <span>低</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-yellow-400 rounded" />
          <span>中低</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-green-400 rounded" />
          <span>中高</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-green-500 rounded" />
          <span>高</span>
        </div>
      </div>
    </div>
  );
}